import { queryOptions } from '@w/react-query/db'
import type { PageRef } from '@w/content-blocks/schema'
import { s } from '@w/schema'
import type { WorkspaceDb } from './workspace-tables'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const pageIdSchema = s.object({ pageId: s.string() })
const titleSchema = s.object({ title: s.string() })

export type PageSearchReference = PageRef & { title: string }

/** Indexed relations select candidates; authored links retain workspace qualification. */
function backlinksQuery(db: WorkspaceDb, sourceSpaceId: string, target: PageRef) {
  const relations = db.pageRelations
  return db.query`
    SELECT DISTINCT ${relations.fromPageId} AS pageId FROM ${relations}
    WHERE ${relations.toPageId.eq(target.pageId)}
      AND ${relations.type.eq('link')}
      AND (instr(${relations.targetSpaces},':'||hex(${target.spaceId})||':')>0
        OR ${sourceSpaceId}=${target.spaceId} AND instr(${relations.targetSpaces},'::')>0)
      AND ${relations.fromPageId.ne(target.pageId)}
    ORDER BY ${relations.fromPageId}
  `
}

export async function pageBacklinks(db: WorkspaceDb, sourceSpaceId: string, target: PageRef): Promise<PageRef[]> {
  const candidates = await backlinksQuery(db, sourceSpaceId, target).execute(pageIdSchema)
  return candidates.map(candidate => ({spaceId:sourceSpaceId,pageId:candidate.pageId}))
}

/** Page title decoding belongs to rendering, after indexed relation lookup. */
export async function pageBacklinkReferences(db: WorkspaceDb, sourceSpaceId: string, target: PageRef): Promise<PageSearchReference[]> {
  const references = await pageBacklinks(db, sourceSpaceId, target)
  const titled: PageSearchReference[] = []
  for (const reference of references) {
    const page = await db.query`SELECT ${db.pages.title} FROM ${db.pages}
      WHERE ${db.pages.id.eq(reference.pageId)}`.executeOne(titleSchema)
    if (page) titled.push({ ...reference, title: page.title })
  }
  return titled.toSorted((left, right) => left.title.localeCompare(right.title))
}

export function pageBacklinksOptions(db: WorkspaceDb, sourceSpaceId: string, target: PageRef) {
  // UI projection subscribes to both topology and labels; page-title edits invalidate it.
  const references = db.pageRelations
  const labels = db.query`SELECT DISTINCT ${db.pages.id}, ${db.pages.title}
    FROM ${references} JOIN ${db.pages} ON ${db.pages.id} = ${references.fromPageId}
    WHERE ${references.toPageId.eq(target.pageId)} AND ${references.type.eq('link')}
      AND ${references.fromPageId.ne(target.pageId)}`
  const options = queryOptions(labels, s.object({ id: db.pages.id.zodType, title: s.string() }), { staleTime: Infinity })
  return { ...options, queryKey: [...options.queryKey, sourceSpaceId, target.spaceId],
    queryFn: () => pageBacklinkReferences(db, sourceSpaceId, target) }
}
